import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ClearanceProfileSketch } from '@/components/ClearanceProfileSketch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/ui/numeric-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { execute } from '@/graphql/execute';
import { MAX_PROFILE_NAME_CHARS } from '@/lib/clearance/dialogModel';
import {
  ROAD_BOX_DEFAULT_BASE_M,
  ROAD_BOX_HEIGHTS,
  STANDARD_PRESETS,
  roadBoxPreset,
  type ClearancePreset,
  type ClearancePresetId,
} from '@/lib/clearance/presets';
import {
  MAX_PROFILE_VERTICES,
  profileIssue,
  rectangleProfile,
  trapezoidProfile,
  type ProfileIssue,
} from '@/lib/clearance/profile';
import type { ClearanceVertex } from '@/lib/clearance/types';
import {
  clearanceProfilesQuery,
  deleteClearanceProfileMutation,
  saveClearanceProfileMutation,
} from '@/queries/clearance.query';

export interface ClearanceProfileEditorValue {
  profileId: string;
  profileName: string;
  /** The published-standard citation (ruling K4), or null for a hand-drawn profile. */
  sourceAttribution: string | null;
  vertices: ClearanceVertex[];
  /**
   * True while a SHIPPED standard preset is selected. Spec §1 ships those
   * read-only: editing "TC E-05 Diagram 1" in place would leave a polygon
   * still carrying that name and attribution while no longer being it.
   * "Save a copy" clears the flag and detaches the citation-bearing identity.
   */
  readOnly: boolean;
}

export interface ClearanceProfileEditorProps {
  value: ClearanceProfileEditorValue;
  onChange: (patch: Partial<ClearanceProfileEditorValue>) => void;
  /** Vertical offset, for the sketch's datum label only. */
  verticalOffsetM?: number;
}

/**
 * Every edit detaches the value from whatever it came from: a saved row's id,
 * and a shipped preset's read-only identity. The run snapshots by value
 * anyway, and pretending a mutated polygon is still "TC E-05 Diagram 1" is
 * exactly what ruling K4's attribution is supposed to prevent.
 *
 * The NAME is not in here because renaming needs the current one — see
 * `detach()` inside the component, which is what every call site actually
 * spreads. Clearing the citation while leaving the name was finding C2: a 4×5 m
 * box would still have run, and been filed, under "TC E-05 Diagram 1".
 *
 * Module-level so it is one frozen literal rather than a fresh object closed
 * over by every `useCallback` on every render.
 */
const DETACH = { profileId: '', readOnly: false, sourceAttribution: null } as const;

/**
 * `ProfileIssue['code']` → its i18n key, written out per code for the same
 * reason `PRESET_NAME_KEYS` is: `t()` is typed against the English bundle, so
 * an interpolated `clearance.profile.issues.${string}` is not a valid key, and
 * `satisfies` makes a new issue code a compile error until its five
 * translations exist (finding I11).
 *
 * The numbers inside the messages stay exactly as the validator produced them
 * (0-based vertex and edge indices), so the inline line and a server-side
 * rejection of the same profile quote the same figures.
 */
const ISSUE_KEYS = {
  notAList: 'clearance.profile.issues.notAList',
  tooFew: 'clearance.profile.issues.tooFew',
  tooMany: 'clearance.profile.issues.tooMany',
  notAPair: 'clearance.profile.issues.notAPair',
  notFinite: 'clearance.profile.issues.notFinite',
  offsetOutOfRange: 'clearance.profile.issues.offsetOutOfRange',
  heightOutOfRange: 'clearance.profile.issues.heightOutOfRange',
  closedRing: 'clearance.profile.issues.closedRing',
  duplicateVertex: 'clearance.profile.issues.duplicateVertex',
  selfIntersects: 'clearance.profile.issues.selfIntersects',
  noArea: 'clearance.profile.issues.noArea',
} as const satisfies Record<ProfileIssue['code'], string>;

/**
 * The typed bridge from a preset's `nameKey` to its i18n key. `t()` is typed
 * against the English bundle, so an interpolated `clearance.presets.${string}`
 * is not a valid key — and a cast would hide exactly the mistake worth
 * catching. Written out per id, `satisfies Record<ClearancePresetId, …>` makes
 * a seventh preset a compile error until its five translations exist.
 */
const PRESET_NAME_KEYS = {
  roadBox: 'clearance.presets.roadBox',
  tcE05Diagram1: 'clearance.presets.tcE05Diagram1',
  tcE05Diagram3: 'clearance.presets.tcE05Diagram3',
  ca8ccr: 'clearance.presets.ca8ccr',
  artcAbcd: 'clearance.presets.artcAbcd',
  artcF: 'clearance.presets.artcF',
} as const satisfies Record<ClearancePresetId, string>;

/**
 * Same typed bridge for the six named legal headrooms of the road clearance
 * box (ruling K1's first preset). `ROAD_BOX_HEIGHTS` owns the metres and the
 * citation; the bundles own the label.
 */
const ROAD_HEIGHT_NAME_KEYS = {
  ukMaintained: 'clearance.profile.roadHeights.ukMaintained',
  ukNew: 'clearance.profile.roadHeights.ukNew',
  ukHighLoad: 'clearance.profile.roadHeights.ukHighLoad',
  usInterstateRural: 'clearance.profile.roadHeights.usInterstateRural',
  usUrbanSecondary: 'clearance.profile.roadHeights.usUrbanSecondary',
  euAgr: 'clearance.profile.roadHeights.euAgr',
} as const satisfies Record<(typeof ROAD_BOX_HEIGHTS)[number]['id'], string>;

/**
 * The shipped presets' names as the user sees them: localised, trimmed and
 * lower-cased for comparison.
 *
 * Exported because THREE guards have to agree on it (finding N1) — the
 * editor's rename-on-detach, the editor's Save refusal, and
 * `deriveClearance`'s Run refusal. A profile carrying one of these names is
 * claiming to BE that published standard, which is only true while `readOnly`
 * is set.
 */
export const useShippedProfileNames = (): ReadonlySet<string> => {
  const { t } = useTranslation('project');
  return useMemo(() => new Set(Object.values(PRESET_NAME_KEYS).map((key) => t(key).trim().toLowerCase())), [t]);
};

/**
 * Where a new vertex goes when the user presses "Add vertex".
 *
 * Stacking it 1 m above the LAST vertex — the obvious implementation — makes
 * the closing edge run back down through the previous corner, so the very
 * first thing a user sees after Rectangle → Add vertex is "The profile crosses
 * itself". A ring has exactly one right answer: the midpoint of the edge being
 * split (here the closing edge, last → first), which is on the outline and can
 * never cross it.
 *
 * Below three vertices there is no ring yet, so the seeds build a
 * non-degenerate triangle instead: a second point 1 m to the right, then an
 * apex offset perpendicular to that edge. Three presses of the button on an
 * empty profile therefore produce a VALID polygon rather than three
 * consecutive error messages.
 */
export const nextVertex = (vertices: readonly ClearanceVertex[]): ClearanceVertex => {
  const a = vertices.at(-1);
  if (!a) return [0, 0];
  if (vertices.length === 1) return [a[0] + 1, a[1]];
  const b = vertices[0];
  if (vertices.length === 2) {
    // Perpendicular to a→b through its midpoint: guarantees a real area, which
    // a third collinear point would not.
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    return [(a[0] + b[0]) / 2 - dy / 2, (a[1] + b[1]) / 2 + dx / 2];
  }
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};

/**
 * Profile picker + preset forms + vertex table + the live sketch.
 *
 * Form-library agnostic on purpose (values in, patches out) so the run dialog
 * owns the form and this component owns nothing but the preset parameters —
 * the same contract `StationVolumesFields` has with its two dialogs.
 */
export const ClearanceProfileEditor = ({ value, onChange, verticalOffsetM = 0 }: ClearanceProfileEditorProps) => {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<'rectangle' | 'trapezoid' | 'roadBox' | 'standard' | 'custom'>('custom');
  // Which shipped standard is loaded, so the button row can say so (N4).
  const [standardId, setStandardId] = useState<ClearancePresetId | null>(null);
  const [rect, setRect] = useState({ widthM: 4, heightM: 5, baseM: 0 });
  const [trap, setTrap] = useState({ bottomWidthM: 8, topWidthM: 5, heightM: 5, baseM: 0 });
  const [road, setRoad] = useState<{
    halfWidthM: number;
    heightId: (typeof ROAD_BOX_HEIGHTS)[number]['id'];
    baseM: number;
  }>({
    halfWidthM: 4,
    heightId: 'ukMaintained',
    baseM: ROAD_BOX_DEFAULT_BASE_M,
  });

  const profiles = useQuery({
    queryKey: ['clearanceProfiles'],
    queryFn: () => execute(clearanceProfilesQuery),
    staleTime: 60_000,
  });

  const issue = profileIssue(value.vertices);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const shippedNames = useShippedProfileNames();
  const nameIsShipped = shippedNames.has(value.profileName.trim().toLowerCase());

  /**
   * Finding C2. Detaching has to rename as well as un-cite: the run snapshots
   * the name into the batch label, the output filename, the CSV header, the
   * layer metadata and the dock header, and a modified gauge filed under
   * "TC E-05 Diagram 1" is a survey signed off against an envelope it is not.
   *
   * The rename fires ONLY while the name is still a shipped standard's — this
   * runs on every vertex keystroke, and rewriting a name the user chose
   * themselves on each one would be unusable. That also makes it idempotent:
   * "Custom (based on X)" is not a shipped name, so it is renamed once.
   */
  const detach = useCallback(
    (): Partial<ClearanceProfileEditorValue> => ({
      ...DETACH,
      ...(shippedNames.has(value.profileName.trim().toLowerCase())
        ? { profileName: t('clearance.profile.detachedName', { name: value.profileName.trim() }) }
        : {}),
    }),
    [shippedNames, t, value.profileName],
  );

  const applyRect = useCallback(
    (next: typeof rect) => {
      setRect(next);
      setStandardId(null);
      onChange({ vertices: rectangleProfile(next), ...detach() });
    },
    [detach, onChange],
  );

  const applyTrap = useCallback(
    (next: typeof trap) => {
      setTrap(next);
      setStandardId(null);
      onChange({ vertices: trapezoidProfile(next), ...detach() });
    },
    [detach, onChange],
  );

  /**
   * The road clearance box (ruling K1, preset #1): a rectangle whose HEIGHT is
   * a published legal headroom rather than a number the user invented, so it
   * carries that standard's citation. Editable — unlike the rail outlines it is
   * parametric, and the half-width is a site-measured quantity (DMRB CD 127
   * applies headroom over the Structure Free Zone, whose width is not fixed).
   */
  const applyRoad = useCallback(
    (next: typeof road) => {
      setRoad(next);
      setStandardId(null);
      const generated = roadBoxPreset(next.halfWidthM, next.heightId, next.baseM);
      onChange({
        // `detach()` first for the C2 rename, then this preset's OWN citation
        // over the null it cleared — the road box carries a published headroom.
        ...detach(),
        vertices: generated.vertices,
        sourceAttribution: generated.sourceAttribution,
      });
    },
    [detach, onChange],
  );

  /** Load a shipped standard preset: read-only, attributed, not editable. */
  const applyStandard = useCallback(
    (standard: ClearancePreset) => {
      setPreset('standard');
      setStandardId(standard.id);
      onChange({
        profileId: '',
        profileName: t(PRESET_NAME_KEYS[standard.id]),
        sourceAttribution: standard.sourceAttribution,
        vertices: standard.vertices,
        readOnly: true,
      });
    },
    [onChange, t],
  );

  /** "Save a copy": keep the geometry and the citation, drop the lock. */
  const saveACopy = useCallback(() => {
    onChange({
      profileId: '',
      profileName: t('clearance.profile.copyOf', { name: value.profileName }),
      readOnly: false,
    });
  }, [onChange, t, value.profileName]);

  const setVertex = useCallback(
    (i: number, axis: 0 | 1, v: number) => {
      const next = value.vertices.map((p, k) => (k === i ? ([...p] as ClearanceVertex) : p));
      next[i][axis] = v;
      onChange({ vertices: next, ...detach() });
    },
    [detach, onChange, value.vertices],
  );

  const save = useMutation({
    mutationFn: () =>
      execute(saveClearanceProfileMutation, {
        input: {
          ...(value.profileId ? { id: value.profileId } : {}),
          name: value.profileName.trim(),
          sourceAttribution: value.sourceAttribution,
          vertices: value.vertices,
        },
      }),
    onSuccess: (data) => {
      onChange({ profileId: data.saveClearanceProfile.id });
      void queryClient.invalidateQueries({ queryKey: ['clearanceProfiles'] });
      // NOT `clearance.profile.saved` — that key is the picker's "Saved
      // profiles" LABEL. Two different strings must not share one key.
      toast.success(t('clearance.profile.savedToast'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => execute(deleteClearanceProfileMutation, { id }),
    onSuccess: () => {
      setConfirmDelete(false);
      onChange({ profileId: '' });
      void queryClient.invalidateQueries({ queryKey: ['clearanceProfiles'] });
      toast.success(t('clearance.profile.deleted'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const savedList = useMemo(() => profiles.data?.clearanceProfiles ?? [], [profiles.data]);

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <Label>{t('clearance.profile.saved')}</Label>
        <Select
          value={value.profileId || '__none'}
          onValueChange={(id) => {
            if (id === '__none') {
              onChange({ profileId: '', profileName: '', sourceAttribution: null, vertices: [], readOnly: false });
              return;
            }
            const found = savedList.find((p) => p.id === id);
            if (!found) return;
            setPreset('custom');
            setStandardId(null);
            onChange({
              profileId: found.id,
              profileName: found.name,
              sourceAttribution: found.sourceAttribution ?? null,
              vertices: found.vertices as ClearanceVertex[],
              // A saved row is the user's own; only SHIPPED presets are locked.
              readOnly: false,
            });
          }}>
          <SelectTrigger data-testid='clearance-profile-picker'>
            <SelectValue placeholder={t('clearance.profile.pickPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='__none'>{t('clearance.profile.none')}</SelectItem>
            {savedList.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Finding I7: without these, a failed fetch is indistinguishable from
            "you have no saved profiles" — and the user re-draws a gauge they
            already had. */}
        {profiles.isLoading && (
          <p data-testid='clearance-profiles-loading' className='text-xs text-slate-500 dark:text-stone-400'>
            {t('clearance.profile.savedLoading')}
          </p>
        )}
        {profiles.error && (
          <div className='flex items-center gap-2 text-xs text-red-600 dark:text-red-400'>
            <span data-testid='clearance-profiles-error'>
              {t('clearance.profile.savedFailed', { message: profiles.error.message })}
            </span>
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-6 px-2'
              data-testid='clearance-profiles-retry'
              onClick={() => void profiles.refetch()}>
              {t('clearance.profile.savedRetry')}
            </Button>
          </div>
        )}
      </div>

      <div className='flex flex-wrap gap-2'>
        <Button
          type='button'
          size='sm'
          variant={preset === 'roadBox' ? 'default' : 'outline'}
          aria-pressed={preset === 'roadBox'}
          data-testid='clearance-preset-roadBox'
          onClick={() => {
            setPreset('roadBox');
            applyRoad(road);
          }}>
          {t('clearance.presets.roadBox')}
        </Button>
        <Button
          type='button'
          size='sm'
          variant={preset === 'rectangle' ? 'default' : 'outline'}
          aria-pressed={preset === 'rectangle'}
          data-testid='clearance-preset-rectangle'
          onClick={() => {
            setPreset('rectangle');
            applyRect(rect);
          }}>
          {t('clearance.profile.presetRectangle')}
        </Button>
        <Button
          type='button'
          size='sm'
          variant={preset === 'trapezoid' ? 'default' : 'outline'}
          aria-pressed={preset === 'trapezoid'}
          data-testid='clearance-preset-trapezoid'
          onClick={() => {
            setPreset('trapezoid');
            applyTrap(trap);
          }}>
          {t('clearance.profile.presetTrapezoid')}
        </Button>
      </div>

      {/* The shipped standards (ruling K1). Read-only: picking one loads its
          published dimensions AND its attribution, and the editor locks until
          the user saves a copy. */}
      <div className='flex flex-col gap-1'>
        <Label>{t('clearance.profile.standards')}</Label>
        <div className='flex flex-wrap gap-2'>
          {STANDARD_PRESETS.map((p) => (
            <Button
              key={p.id}
              type='button'
              size='sm'
              variant={standardId === p.id ? 'default' : 'outline'}
              aria-pressed={standardId === p.id}
              // K4 asks for the source line "in the picker" — a title puts it
              // on the row itself, before anything is loaded.
              title={p.sourceAttribution}
              data-testid={`clearance-preset-${p.id}`}
              onClick={() => applyStandard(p)}>
              {t(PRESET_NAME_KEYS[p.id])}
            </Button>
          ))}
        </div>
      </div>

      {value.sourceAttribution && (
        <p
          data-testid='clearance-profile-attribution'
          className='text-xs leading-snug font-medium text-slate-700 dark:text-stone-200'>
          {value.sourceAttribution}
        </p>
      )}

      {value.readOnly && (
        <div className='flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs dark:border-amber-700 dark:bg-amber-950'>
          <span data-testid='clearance-profile-readonly' className='flex-1'>
            {t('clearance.profile.readOnlyNote')}
          </span>
          <Button type='button' size='sm' variant='outline' data-testid='clearance-profile-copy' onClick={saveACopy}>
            {t('clearance.profile.saveACopy')}
          </Button>
        </div>
      )}

      {preset === 'roadBox' && (
        <div className='grid grid-cols-3 gap-2'>
          <div>
            <Label htmlFor='clearance-roadbox-halfwidth'>{t('clearance.profile.halfWidth')}</Label>
            <NumericField
              id='clearance-roadbox-halfwidth'
              data-testid='clearance-roadbox-halfwidth'
              value={road.halfWidthM}
              onChange={(v) => applyRoad({ ...road, halfWidthM: v })}
              step='0.01'
            />
          </div>
          <div>
            {/* The floor, not the roof. Without it the box starts flush with
                the carriageway and the road surface reads as an obstruction —
                the single biggest source of meaningless violations on a road
                run. The published headroom above stays measured from the
                carriageway whatever this is set to. */}
            <Label htmlFor='clearance-roadbox-base'>{t('clearance.profile.roadBase')}</Label>
            <NumericField
              id='clearance-roadbox-base'
              data-testid='clearance-roadbox-base'
              value={road.baseM}
              onChange={(v) => applyRoad({ ...road, baseM: v })}
              step='0.01'
              min='0'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.roadHeight')}</Label>
            <Select
              value={road.heightId}
              onValueChange={(id) => {
                const height = ROAD_BOX_HEIGHTS.find((h) => h.id === id);
                // `roadBoxPreset` throws on an unknown id rather than quietly
                // substituting a different legal headroom; don't hand it one.
                if (height) applyRoad({ ...road, heightId: height.id });
              }}>
              <SelectTrigger data-testid='clearance-roadbox-height'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROAD_BOX_HEIGHTS.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {t(ROAD_HEIGHT_NAME_KEYS[h.id])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {preset === 'rectangle' && (
        <div className='grid grid-cols-3 gap-2'>
          <div>
            <Label>{t('clearance.profile.width')}</Label>
            <NumericField
              data-testid='clearance-rect-width'
              value={rect.widthM}
              onChange={(v) => applyRect({ ...rect, widthM: v })}
              step='0.01'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.height')}</Label>
            <NumericField
              data-testid='clearance-rect-height'
              value={rect.heightM}
              onChange={(v) => applyRect({ ...rect, heightM: v })}
              step='0.01'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.base')}</Label>
            <NumericField
              data-testid='clearance-rect-base'
              value={rect.baseM}
              onChange={(v) => applyRect({ ...rect, baseM: v })}
              step='0.01'
            />
          </div>
        </div>
      )}

      {preset === 'trapezoid' && (
        <div className='grid grid-cols-4 gap-2'>
          <div>
            <Label>{t('clearance.profile.bottomWidth')}</Label>
            <NumericField
              data-testid='clearance-trap-bottom'
              value={trap.bottomWidthM}
              onChange={(v) => applyTrap({ ...trap, bottomWidthM: v })}
              step='0.01'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.topWidth')}</Label>
            <NumericField
              data-testid='clearance-trap-top'
              value={trap.topWidthM}
              onChange={(v) => applyTrap({ ...trap, topWidthM: v })}
              step='0.01'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.height')}</Label>
            <NumericField
              data-testid='clearance-trap-height'
              value={trap.heightM}
              onChange={(v) => applyTrap({ ...trap, heightM: v })}
              step='0.01'
            />
          </div>
          <div>
            <Label>{t('clearance.profile.base')}</Label>
            <NumericField
              data-testid='clearance-trap-base'
              value={trap.baseM}
              onChange={(v) => applyTrap({ ...trap, baseM: v })}
              step='0.01'
            />
          </div>
        </div>
      )}

      <div className='flex gap-3'>
        <div className='min-w-0 flex-1'>
          <Label>{t('clearance.profile.vertices')}</Label>
          <div className='max-h-52 overflow-auto rounded border border-slate-200 dark:border-stone-700'>
            <table className='w-full text-xs'>
              <thead className='sticky top-0 bg-slate-50 dark:bg-stone-800'>
                <tr>
                  <th scope='col' className='px-1 py-1 text-left'>
                    #
                  </th>
                  {/* The sign convention is STATED in the dock's table (finding
                      I5); here it is a tooltip on the column that carries it,
                      so the editor does not have to repeat the sentence. */}
                  <th scope='col' className='px-1 py-1 text-right' title={t('clearance.offsetConvention')}>
                    {t('clearance.profile.offset')}
                  </th>
                  <th scope='col' className='px-1 py-1 text-right'>
                    {t('clearance.profile.heightShort')}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {value.vertices.map((p, i) => (
                  <tr key={i}>
                    {/* 1-based for the reader; the test ids stay 0-based
                        because they are indexes, not labels. */}
                    <td className='px-1 py-0.5 tabular-nums text-slate-500'>{i + 1}</td>
                    <td className='px-1 py-0.5'>
                      <NumericField
                        data-testid={`clearance-vertex-offset-${i}`}
                        aria-label={t('clearance.profile.vertexOffsetLabel', { index: i + 1 })}
                        value={p[0]}
                        onChange={(v) => setVertex(i, 0, v)}
                        step='0.01'
                        disabled={value.readOnly}
                      />
                    </td>
                    <td className='px-1 py-0.5'>
                      <NumericField
                        data-testid={`clearance-vertex-height-${i}`}
                        aria-label={t('clearance.profile.vertexHeightLabel', { index: i + 1 })}
                        value={p[1]}
                        onChange={(v) => setVertex(i, 1, v)}
                        step='0.01'
                        disabled={value.readOnly}
                      />
                    </td>
                    <td className='px-1 py-0.5'>
                      <Button
                        type='button'
                        size='icon'
                        variant='ghost'
                        data-testid={`clearance-vertex-remove-${i}`}
                        aria-label={t('clearance.profile.removeVertex', { index: i + 1 })}
                        // Only the read-only lock disables this. Dropping below
                        // three vertices is allowed and then NAMED by the
                        // validation line — a button that silently stops
                        // working teaches nobody why two points aren't a
                        // profile, and Save stays blocked either way.
                        disabled={value.readOnly}
                        onClick={() => onChange({ vertices: value.vertices.filter((_, k) => k !== i), ...detach() })}>
                        <Trash2 className='size-3.5' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='mt-1'
            data-testid='clearance-vertex-add'
            disabled={value.readOnly || value.vertices.length >= MAX_PROFILE_VERTICES}
            // The upper bound is a hard disable (there is no "too large"
            // message to show), so the button says why it stopped working.
            title={
              value.vertices.length >= MAX_PROFILE_VERTICES
                ? t('clearance.profile.maxVerticesHint', { max: MAX_PROFILE_VERTICES })
                : undefined
            }
            onClick={() => onChange({ vertices: [...value.vertices, nextVertex(value.vertices)], ...detach() })}>
            <Plus className='mr-1 size-3.5' />
            {t('clearance.profile.addVertex')}
          </Button>
        </div>
        <ClearanceProfileSketch vertices={value.vertices} verticalOffsetM={verticalOffsetM} />
      </div>

      {issue && (
        <p data-testid='clearance-profile-error' className='text-xs text-red-600 dark:text-red-400'>
          {/* Finding I11: BOTH halves are translated now. The English sentence
              `profileError` returns is still byte-identical to the server
              validator and the worker's `clearance_profile.py` — it is what a
              server-side rejection surfaces — but the inline message the user
              reads while typing goes through the issue CODE the validator now
              carries alongside it, so it is not the one English paragraph in a
              dialog of five-language labels. */}
          {t('clearance.profile.invalidLead')}{' '}
          <span data-testid='clearance-profile-error-detail'>{t(ISSUE_KEYS[issue.code], { ...issue })}</span>
        </p>
      )}

      <div className='flex items-end gap-2'>
        <div className='min-w-0 flex-1'>
          <Label>{t('clearance.profile.name')}</Label>
          <Input
            data-testid='clearance-profile-name'
            value={value.profileName}
            maxLength={MAX_PROFILE_NAME_CHARS}
            disabled={value.readOnly}
            onChange={(e) => onChange({ profileName: e.target.value })}
          />
        </div>
        <Button
          type='button'
          data-testid='clearance-profile-save'
          // `nameIsShipped` is the second half of finding C2: detaching renames
          // automatically, but nothing stopped a user typing "TC E-05 Diagram
          // 1" over their own polygon and saving THAT as an org-shared row.
          disabled={!!issue || value.readOnly || !value.profileName.trim() || nameIsShipped || save.isPending}
          onClick={() => {
            if (nameIsShipped) return;
            save.mutate();
          }}>
          {t('clearance.profile.save')}
        </Button>
        {value.profileId && (
          /* Finding I6: a saved profile is visible to the whole organisation
             and the delete is irreversible, so it goes through the same
             AlertDialog the org danger zone uses rather than firing on one
             click. */
          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogTrigger asChild>
              <Button
                type='button'
                variant='outline'
                data-testid='clearance-profile-delete'
                disabled={remove.isPending}>
                {t('clearance.profile.delete')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('clearance.profile.deleteConfirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription data-testid='clearance-profile-delete-body'>
                  {t('clearance.profile.deleteConfirmBody', { name: value.profileName })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid='clearance-profile-delete-cancel'>
                  {t('clearance.profile.deleteConfirmCancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid='clearance-profile-delete-confirm'
                  variant='destructive'
                  onClick={() => remove.mutate(value.profileId)}>
                  {t('clearance.profile.deleteConfirmAction')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {nameIsShipped && !value.readOnly && (
        <p data-testid='clearance-profile-name-error' className='text-xs text-red-600 dark:text-red-400'>
          {t('clearance.profile.nameReserved')}
        </p>
      )}
    </div>
  );
};
